<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="revolve-cut-keep-dialog-viewport"
    >
      <div
        class="revolve-cut-keep-dialog-shell"
        :style="dialogShellStyle"
        @click.stop
      >
        <n-card
          size="small"
          role="dialog"
          aria-modal="true"
          class="revolve-cut-keep-dialog-card"
        >
          <template #header>
            <div
              class="revolve-cut-keep-dialog-header"
              @pointerdown="handleDialogHeaderPointerDown"
            >
              <span class="revolve-cut-keep-dialog-title">要保留的实体</span>
            </div>
          </template>

          <div class="dialog-body">
            <n-radio-group
              :value="selectionMode"
              @update:value="updateSelectionMode"
            >
              <div class="mode-list">
                <n-radio value="all">所有实体</n-radio>
                <n-radio value="selected">所选实体</n-radio>
              </div>
            </n-radio-group>

            <div v-if="selectionMode === 'selected'" class="fragment-list">
              <button
                v-for="fragment in fragments"
                :key="`${fragment.sourceSolidId}:${fragment.shapeKey}`"
                type="button"
                class="fragment-row"
                :class="{ 'fragment-row--selected': isDraftSelected(fragment.sourceSolidId, fragment.shapeKey) }"
                @click="emit('toggle-fragment', fragment.sourceSolidId, fragment.shapeKey)"
              >
                <n-checkbox
                  size="small"
                  :checked="isDraftSelected(fragment.sourceSolidId, fragment.shapeKey)"
                  @click.stop
                />
                <span class="fragment-label">{{ fragment.label }}</span>
              </button>
            </div>
          </div>

          <template #footer>
            <div class="dialog-footer">
              <n-button size="small" @click="emit('cancel')">取消</n-button>
              <n-button size="small" type="primary" @click="emit('confirm')">确定</n-button>
            </div>
          </template>
        </n-card>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import {
  NButton,
  NCard,
  NCheckbox,
  NRadio,
  NRadioGroup,
} from 'naive-ui';
import { useFloatingDialogDrag } from '@/composables/dialog/useFloatingDialogDrag';

interface RevolveCutKeepBodyFragment {
  sourceSolidId: string;
  shapeKey: string;
  label: string;
}

const props = defineProps<{
  show: boolean;
  fragments: RevolveCutKeepBodyFragment[];
  selectionMode: 'all' | 'selected';
  draftSelectedShapeKeys: Record<string, string[]>;
}>();

const emit = defineEmits<{
  'update:selection-mode': [mode: 'all' | 'selected'];
  'toggle-fragment': [sourceSolidId: string, shapeKey: string];
  confirm: [];
  cancel: [];
}>();

const dialogDragController = useFloatingDialogDrag();

const dialogShellStyle = dialogDragController.shellStyle;

/**
 * 保留实体弹窗每次打开都应回到默认居中位置，
 * 避免上一次操作遗留的位置影响下一次切除流程。
 */
watch(
  () => props.show,
  (visible) => {
    if (!visible) {
      dialogDragController.resetPosition();
      return;
    }
    dialogDragController.resetPosition();
  },
);

/**
 * 更新选择模式。
 * @param value NaiveUI Radio 的原始值。
 */
function updateSelectionMode(value: string | number | boolean): void {
  emit('update:selection-mode', value === 'selected' ? 'selected' : 'all');
}

/**
 * 标题栏按下时启动拖拽。
 * @param event 指针事件。
 */
function handleDialogHeaderPointerDown(event: PointerEvent): void {
  dialogDragController.handlePointerDown(event);
}

/**
 * 判断当前片段是否已处于草稿选中状态。
 * @param sourceSolidId 来源实体 ID。
 * @param shapeKey 片段稳定键。
 * @returns 是否已选中。
 */
function isDraftSelected(sourceSolidId: string, shapeKey: string): boolean {
  return (props.draftSelectedShapeKeys[sourceSolidId] ?? []).includes(shapeKey);
}
</script>

<style scoped>
.revolve-cut-keep-dialog-viewport {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  box-shadow: none !important;
}

.revolve-cut-keep-dialog-shell {
  pointer-events: auto;
}

.revolve-cut-keep-dialog-card {
  border-radius: 6px;
  box-shadow: none;
}

.revolve-cut-keep-dialog-header {
  display: flex;
  align-items: center;
  min-height: 28px;
  cursor: move;
  user-select: none;
}

.revolve-cut-keep-dialog-title {
  font-size: 14px;
  font-weight: 600;
}

.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mode-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fragment-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}

.fragment-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 30px;
  padding: 5px 8px;
  border: 1px solid var(--n-border-color);
  border-radius: 6px;
  color: var(--n-text-color);
  background: var(--n-color);
  cursor: pointer;
  text-align: left;
}

.fragment-row:hover,
.fragment-row--selected {
  border-color: var(--n-primary-color);
  background: var(--n-primary-color-suppl);
}

.fragment-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
